'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowRight, ArrowLeft, Check, AlertCircle, Mail } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { capture, getPostHogClient } from '@/lib/analytics/posthog';
import {
  COMPANY_SIZES,
  DEPARTMENTS,
  INDUSTRIES,
  AI_TOOLS,
} from '@/lib/constants';
import type { CompanySize, Department, Industry, AiTool } from '@/lib/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuthTwoPane } from '@/components/auth/AuthTwoPane';
import { SignupEditorial } from '@/components/auth/editorial/SignupEditorial';

// 'check-email' is a terminal state, not a numbered step — it is deliberately
// absent from STEPS so the progress indicator is hidden once we reach it.
type Step = 'credentials' | 'about' | 'tools' | 'check-email';

const STEPS: { key: Step; label: string }[] = [
  { key: 'credentials', label: 'Account' },
  { key: 'about', label: 'About You' },
  { key: 'tools', label: 'AI Tools' },
];

export default function SignupPage() {
  const router = useRouter();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [companySize, setCompanySize] = useState<CompanySize | ''>('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState<Department | ''>('');
  const [industry, setIndustry] = useState<Industry | ''>('');
  const [aiTools, setAiTools] = useState<AiTool[]>([]);
  const [useCase, setUseCase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signupTracked = useRef(false);
  useEffect(() => {
    if (!signupTracked.current) {
      signupTracked.current = true;
      capture('signup_page_viewed', {});
    }
  }, []);

  function stepIndex(s: Step): number {
    return STEPS.findIndex((st) => st.key === s);
  }

  function toggleTool(tool: AiTool) {
    setAiTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  }

  function handleCredentialsNext(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    if (!password || password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError(null);
    capture('signup_step_completed', { step: 'credentials', step_number: 1 });
    setStep('about');
  }

  function handleAboutNext(e: React.FormEvent) {
    e.preventDefault();
    if (!orgName.trim()) { setError('Organization name is required.'); return; }
    if (!companySize) { setError('Please select your company size.'); return; }
    if (!department) { setError('Please select your department.'); return; }
    if (!industry) { setError('Please select your industry.'); return; }
    setError(null);
    capture('signup_step_completed', { step: 'about', step_number: 2, company_size: companySize, department, industry });
    setStep('tools');
  }

  async function handleGoogleSignup() {
    setSubmitting(true);
    setError(null);
    capture('signup_started', { method: 'google' });
    try {
      const supabase = createClient();
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback?next=/onboarding/connect` },
      });
      if (authError) throw new Error(authError.message);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google sign up failed. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (aiTools.length === 0) { setError('Please select at least one AI tool you use.'); return; }
    setError(null);
    setSubmitting(true);
    capture('signup_started', { method: 'email' });
    try {
      const supabase = createClient();
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        // Route the confirmation link through /auth/callback, which exchanges
        // the code for a session and provisions the org server-side (the same
        // path Google OAuth already uses). Without this the link lands on the
        // Supabase Site URL and the account is never provisioned.
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=/onboarding/connect`,
          // Carry the step 2/3 answers on the auth user. Without this they are
          // lost whenever confirmation is required, because the user never
          // returns to this page state after clicking the link.
          data: {
            org_name: orgName.trim(),
            company_size: companySize,
            job_title: jobTitle.trim(),
            department,
            industry,
            ai_tools: aiTools,
            use_case: useCase.trim(),
          },
        },
      });
      if (signUpError) {
        if (signUpError.message.toLowerCase().includes('already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw new Error(signUpError.message);
      }
      if (!authData.user) throw new Error('Signup failed — no user returned.');

      // When email confirmation is enabled, signUp() returns a user with no
      // session. There is no auth cookie yet, so /provision would 401 — stop
      // here and tell the user to check their mail. /auth/callback finishes
      // provisioning when they click through.
      if (!authData.session) {
        capture('signup_step_completed', { step: 'tools', step_number: 3, ai_tools: aiTools, has_use_case: Boolean(useCase.trim()) });
        capture('signup_awaiting_confirmation', { method: 'email' });
        setStep('check-email');
        return;
      }

      const provisionRes = await fetch('/api/v1/auth/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!provisionRes.ok) {
        const body = await provisionRes.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Provisioning failed. Please try again.');
      }

      capture('signup_step_completed', { step: 'tools', step_number: 3, ai_tools: aiTools, has_use_case: Boolean(useCase.trim()) });
      const ph = getPostHogClient();
      if (ph) {
        ph.identify(authData.user.id, { email: authData.user.email, signup_method: 'email' });
        ph.people.set({ org_name: orgName.trim(), company_size: companySize, job_title: jobTitle.trim(), department, industry, ai_tools: aiTools, signup_status: 'active' });
      }
      capture('signup_completed', { method: 'email', ai_tools_count: aiTools.length });
      router.push('/onboarding/connect');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Signup failed. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Shared form content — reused by both V1 and V2 layout paths
  // ---------------------------------------------------------------------------
  const formContent = (
    <>
      <h1 className="mb-2 text-2xl font-semibold uppercase tracking-tight text-ink">
        {step === 'credentials' && 'Create your account'}
        {step === 'about' && 'About you'}
        {step === 'tools' && 'Your AI stack'}
        {step === 'check-email' && 'Confirm your email'}
      </h1>
      <p className="mb-6 text-sm text-graphite">
        {step === 'credentials' && 'Start tracking your AI spend in minutes'}
        {step === 'about' && 'Help us understand your organization'}
        {step === 'tools' && 'Which AI tools does your team use?'}
        {step === 'check-email' && 'One last step before your workspace is ready'}
      </p>

      {/* Progress indicator — hidden on the terminal confirmation state */}
      <div className={`mb-6 flex items-center gap-2 ${step === 'check-email' ? 'hidden' : ''}`}>
        {STEPS.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            {i > 0 && <div className="mx-1 h-0.5 w-6 bg-rule" />}
            <div className="flex items-center gap-2">
              <div
                className={`flex h-6 w-6 items-center justify-center text-[11px] font-bold ${
                  stepIndex(step) > i
                    ? 'border border-salmon bg-salmon text-white'
                    : stepIndex(step) === i
                      ? 'border border-salmon bg-salmon text-white'
                      : 'border border-rule bg-paper text-ink'
                }`}
              >
                {stepIndex(step) > i ? <Check size={12} strokeWidth={3} /> : i + 1}
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-[0.1em] ${stepIndex(step) >= i ? 'text-ink' : 'text-graphite'}`}>
                {s.label}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Form card */}
      <div className="rounded-sm border border-rule bg-paper p-8">
        {error && (
          <div className="mb-5 bg-vellum border-l-[3px] border-l-burn rounded-sm p-4 text-sm text-ink flex items-start gap-2" role="alert">
            <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-burn" />
            <span>{error}</span>
          </div>
        )}

        {/* Terminal state: account created, awaiting email confirmation */}
        {step === 'check-email' && (
          <div className="space-y-4" role="status">
            <div className="flex items-start gap-3">
              <Mail aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-salmon" />
              <div className="space-y-2">
                <p className="text-sm text-ink">
                  We sent a confirmation link to <span className="font-semibold">{email.trim()}</span>.
                </p>
                <p className="text-sm text-graphite">
                  Click it to activate your account and finish setting up your workspace.
                  You can close this tab — the link opens Verbal for you.
                </p>
              </div>
            </div>
            <p className="text-sm text-graphite">
              Nothing in your inbox after a minute? Check spam, or{' '}
              <Link href="/login" className="font-medium text-salmon underline underline-offset-2">
                sign in
              </Link>{' '}
              once you have confirmed.
            </p>
          </div>
        )}

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <>
            <form onSubmit={handleCredentialsNext} className="space-y-5">
              <div>
                <label htmlFor="signup-email" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">
                  Email address
                </label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(null); }}
                  autoComplete="email"
                  spellCheck={false}
                  placeholder="you@company.com"
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <label htmlFor="signup-password" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">
                  Password
                </label>
                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  autoComplete="new-password"
                  placeholder="Minimum 8 characters"
                  className="mt-1 w-full"
                />
                <p className="mt-1 text-[11px] text-graphite">Must be at least 8 characters</p>
              </div>
              <Button type="submit" className="w-full">
                Continue
                <ArrowRight size={12} strokeWidth={3} />
              </Button>
            </form>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-0.5 flex-1 bg-vellum" />
              <span aria-hidden="true" className="text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">or</span>
              <div className="h-0.5 flex-1 bg-vellum" />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleGoogleSignup}
              disabled={submitting}
              aria-label="Sign up with Google"
              className="mt-4 w-full"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign up with Google
            </Button>
          </>
        )}

        {/* Step 2: About You */}
        {step === 'about' && (
          <form onSubmit={handleAboutNext} className="space-y-5">
            <div>
              <label htmlFor="org-name" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Organization name</label>
              <Input id="org-name" type="text" value={orgName} onChange={(e) => { setOrgName(e.target.value); setError(null); }} autoFocus placeholder="Acme Corp" className="mt-1 w-full" />
            </div>
            <div>
              <label htmlFor="company-size" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Company size</label>
              <Select value={companySize} onValueChange={(val) => { setCompanySize(val as CompanySize); setError(null); }}>
                <SelectTrigger id="company-size" className="mt-1 w-full"><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent>
                  {COMPANY_SIZES.map((size) => <SelectItem key={size} value={size}>{size} employees</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="job-title" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Your role / title</label>
              <Input id="job-title" type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="VP of Engineering" className="mt-1 w-full" />
            </div>
            <div>
              <label htmlFor="department" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Department</label>
              <Select value={department} onValueChange={(val) => { setDepartment(val as Department); setError(null); }}>
                <SelectTrigger id="department" className="mt-1 w-full"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="industry" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Industry</label>
              <Select value={industry} onValueChange={(val) => { setIndustry(val as Industry); setError(null); }}>
                <SelectTrigger id="industry" className="mt-1 w-full"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((ind) => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { capture('signup_step_back', { from_step: 'about', to_step: 'credentials' }); setStep('credentials'); setError(null); }}>
                <ArrowLeft size={12} strokeWidth={3} />
                Back
              </Button>
              <Button type="submit" className="flex-1">
                Continue
                <ArrowRight size={12} strokeWidth={3} />
              </Button>
            </div>
          </form>
        )}

        {/* Step 3: AI Tools */}
        {step === 'tools' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">Which AI tools do you use?</p>
              <p className="mt-1 text-[11px] text-graphite">Select all that apply</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {AI_TOOLS.map((tool) => (
                  <button
                    key={tool.value}
                    type="button"
                    onClick={() => toggleTool(tool.value)}
                    className={`flex items-center gap-2 border px-3 py-2.5 text-left text-xs font-medium transition-colors ${
                      aiTools.includes(tool.value)
                        ? 'border-salmon bg-salmon/5 text-salmon-text'
                        : 'border-rule text-ink hover:border-graphite'
                    }`}
                  >
                    <div className={`flex h-4 w-4 shrink-0 items-center justify-center border ${aiTools.includes(tool.value) ? 'border-salmon bg-salmon text-white' : 'border-rule'}`}>
                      {aiTools.includes(tool.value) && <Check size={10} strokeWidth={3} />}
                    </div>
                    {tool.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="use-case" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">
                What do you want to track with Verbal?
              </label>
              <Textarea
                id="use-case"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                placeholder="e.g. Track team AI spend across OpenAI and Anthropic, set budget alerts…"
                rows={3}
                className="mt-1 w-full resize-none"
              />
              <p className="mt-1 text-[11px] text-graphite">Optional — helps us prioritize features</p>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { capture('signup_step_back', { from_step: 'tools', to_step: 'about' }); setStep('about'); setError(null); }}>
                <ArrowLeft size={12} strokeWidth={3} />
                Back
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Creating account…' : 'Create account'}
                {!submitting && <ArrowRight size={12} strokeWidth={3} />}
              </Button>
            </div>
          </form>
        )}

        {/* Login link */}
        <div className="mt-6 border-t border-rule pt-4 text-center">
          <p className="text-sm text-graphite">
            Already have an account?{' '}
            <Link href="/login" className="font-bold text-salmon-text hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </>
  );

  // ---------------------------------------------------------------------------
  // V2: two-pane editorial layout
  // ---------------------------------------------------------------------------
  return (
    <AuthTwoPane editorial={<SignupEditorial />}>
      {formContent}
    </AuthTwoPane>
  );
}
